// ============================================================================
// PHONE UTILITIES - FRONTEND
// ============================================================================

import { parsePhoneNumber, isValidPhoneNumber, AsYouType } from 'libphonenumber-js';

/**
 * Formatar número de telefone para padrão brasileiro
 * @param phone - Número sem formatação (ex: "5511999999999")
 * @returns Número formatado (ex: "+55 11 99999-9999")
 */
export function formatPhone(phone: string | null | undefined): string {
  if (!phone) return '';
  
  try {
    // Tentar usar libphonenumber-js para formatar
    const phoneNumber = parsePhoneNumber(phone);
    if (phoneNumber) {
      return phoneNumber.formatInternational();
    }
  } catch (error) {
    // Se falhar, usar formatação manual
  }
  
  // Remove tudo que não é número
  const cleaned = phone.replace(/\D/g, '');
  
  // Formato brasileiro completo: +55 11 99999-9999 (13 dígitos)
  if (cleaned.startsWith('55') && cleaned.length === 13) {
    return `+55 ${cleaned.slice(2, 4)} ${cleaned.slice(4, 9)}-${cleaned.slice(9)}`;
  }
  
  // Formato brasileiro sem código do país: 11 99999-9999 (11 dígitos)
  if (cleaned.length === 11) {
    return `+55 ${cleaned.slice(0, 2)} ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
  }
  
  // Formato com 10 dígitos (telefone fixo): 11 3333-4444
  if (cleaned.length === 10) {
    return `+55 ${cleaned.slice(0, 2)} ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
  }
  
  // Formato genérico com código de país
  if (cleaned.length > 10) {
    return `+${cleaned.slice(0, 2)} ${cleaned.slice(2)}`;
  }
  
  // Retornar original se não se encaixar em nenhum formato
  return phone;
}

/**
 * Extrair número de telefone do JID do WhatsApp
 * @param jid - JID completo (ex: "5511999999999@s.whatsapp.net")
 * @returns Número formatado ou null
 */
export function extractPhoneFromJid(jid: string | null | undefined): string | null {
  if (!jid) return null;
  
  try {
    // Remover @s.whatsapp.net e qualquer outro domínio
    const phone = jid.split('@')[0];
    
    // Validar se é um número válido (apenas dígitos)
    if (!/^\d+$/.test(phone)) return null;
    
    return phone;
  } catch {
    return null;
  }
}

/**
 * Validar se um número de telefone é válido (suporta números internacionais)
 * @param phone - Número de telefone
 * @returns true se válido
 */
export function isValidPhone(phone: string | null | undefined): boolean {
  if (!phone) return false;
  
  const trimmed = phone.trim();
  if (trimmed.startsWith('+')) {
    try {
      return isValidPhoneNumber(trimmed);
    } catch (error) {
      // Se falhar, cai no fallback manual
    }
  }
  
  // Fallback: validação manual para números brasileiros
  const cleaned = phone.replace(/\D/g, '');
  
  // Telefone brasileiro deve ter 10 ou 11 dígitos (com DDD)
  // Ou 12-13 dígitos (com código do país +55)
  // Números internacionais geralmente têm 7-15 dígitos (incluindo código do país)
  return cleaned.length >= 7 && cleaned.length <= 15;
}

/**
 * Obter link do WhatsApp para um número
 * @param phone - Número de telefone
 * @returns URL do WhatsApp Web/API
 */
export function getWhatsAppLink(phone: string | null | undefined): string {
  if (!phone) return '';
  
  const cleaned = phone.replace(/\D/g, '');
  return `https://wa.me/${cleaned}`;
}
